import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseConfigurableComponent } from '@app/base-config.component';
import { BannerComponent } from '@app/components/shared/banner/banner.component';
import { QuestionComponent } from '@app/components/shared/form/question/question.component';
import { InputType } from '@app/enum/input-type';
import { QuestionBase } from '@app/models/question-base';
import { RadioQuestion } from '@app/models/questions/question-radio';
import { TextboxQuestion } from '@app/models/questions/question-textbox';
import { Appointment } from '@app/services/fetch/appointment';
import { Completion } from '@app/services/fetch/completion';
import { QuestionControlService } from '@app/services/question/question-control.service';

@Component({
  selector: 'afcu-check-in',
  standalone: true,
  providers: [QuestionControlService],
  imports: [ReactiveFormsModule, QuestionComponent, BannerComponent],
  templateUrl: './check-in.component.html',
  styleUrl: './check-in.component.css'
})
export class CheckInComponent extends BaseConfigurableComponent{

  private readonly qcs = inject(QuestionControlService)
  protected readonly appointmnent = inject(Appointment)

  questions: QuestionBase<string>[] = [this.FormRadio, this.FormInputPhone]
  inputType : typeof InputType = InputType
  form: FormGroup = this.qcs.toFormGroup(this.questions as QuestionBase<string>[]);
  isSubmitted: boolean = false
  force: boolean = false

  constructor() {
    super('page')
    this.getOrigin()
    this.appointmnent.branchID.set(this.branch)
    
    // Disable automatic error navigation for this component
    this.appointmnent.disableAutoNavigation.set(true)
    
    // Clear errors when user starts typing
    this.form.get('checkInInfo')?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined) {
        // User is typing, clear previous errors to give them a fresh start
        this.appointmnent.unhandledError.set(false)
        this.isSubmitted = false
        this.force = false
        
        // If field is completely cleared, also reset the request
        if (value.trim() === '') {
          this.appointmnent.request.set(null)
        }
      }
    })
  }

  get FormRadio () {
    return new RadioQuestion ({
      key: 'checkInType',
      externalFunction: "checkInType",
      value: 'phone',
      options: [
        { 
          text: this.getText().checkIn.form.phone.label,
          value: "phone",
          selected: true
        },
        { 
          text: this.getText().checkIn.form.email.label,
          value: "email",
          selected: false
        }
      ],
      order: 0
    })
  }

  get FormInputPhone () {
    return new TextboxQuestion ({
      key: 'checkInInfo',
      label: this.getText().checkIn.form.phone.label,
      placeholder: this.getText().checkIn.form.phone.placeholder,
      errorText: this.getText().checkIn.form.phone.error,
      patternValidator: '^\\d{10}$|^\\d{3}-\\d{3}-\\d{4}$',
      minLength: 10,
      maxLength: 12,
      phoneMask: true,
      required: true,
      order: 1
    })
  }

  selectEvent(event: { function: string; value: string | CategorySelected | Branch }): void {
    if (event.function === 'checkInType'){
      this.updateQuestion(event.value as string)
    }
  }

  updateQuestion (checkInType: string) {

    this.force = false
    this.isSubmitted = false
    
    const control = this.form.get('checkInInfo');
    const question = this.questions.find(q => q.key === 'checkInInfo');
  
    if (!control || !question) return;

    const isPhone = checkInType === 'phone';

    // Update question properties
    question.label = isPhone ? this.getText().checkIn.form.phone.label : this.getText().checkIn.form.email.label;
    question.placeholder = isPhone ? this.getText().checkIn.form.phone.placeholder : this.getText().checkIn.form.email.placeholder;
    question.errorText = isPhone
      ? this.getText().checkIn.form.phone.error
      : this.getText().checkIn.form.email.error;
    question.phoneMask = isPhone;

    // Set validators
    const validators = isPhone ? 
      [ 
        Validators.pattern('^\\d{10}$|^\\d{3}-\\d{3}-\\d{4}$')
      ] : [
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.(com|org|net|gov|edu|co|io|info|biz)$')
      ];

    control.setValidators([...validators, Validators.required]);
    control.setValue('');
    control.updateValueAndValidity();

    // Clear all appointment-related errors when switching input types
    this.appointmnent.request.set(null)
    this.appointmnent.unhandledError.set(false)

    // Update the entire form if needed
    this.qcs.toFormGroup(this.questions as QuestionBase<string>[]).updateValueAndValidity();    
  }

  validateAndSetError(controlName: string): void {
    const control = this.form.get(controlName)
    const errors = control?.errors

    if (errors) {
      control?.setErrors({ 'error': true })
    }
  }

  get Error(){
    if (this.appointmnent.error())
      if(this.appointmnent.error().status === 404){
        this.isSubmitted = false
        return true
      }
    return false
  }

  // Computed property to determine which error to show (only one at a time)
  get errorToShow() {
    // Priority order: form validation errors > 404 errors > unhandled errors
    
    // 1. Form validation errors (highest priority)
    if (!this.form.valid && this.isSubmitted && this.force) {
      return { type: 'validation', content: this.getText().checkIn.banner.force }
    }
    
    // 2. 404 errors (appointment not found)
    if (this.Error && this.form.valid) {
      return { type: 'notFound', content: this.getText().checkIn.banner.error }
    }
    
    // 3. Unhandled system errors (lowest priority)
    if (this.appointmnent.unhandledError() && this.form.valid && !this.Error) {
      return { type: 'system', content: this.getText().checkIn.banner.system }
    }
    
    return null
  }

  onSubmit() {
    this.questions.forEach( question => this.validateAndSetError(question.key))
    this.isSubmitted = true

    let checkinInformsation: CheckInPost = {
      email: '',
      phoneNumber: ''
    }

    if (this.form.valid && this.isSubmitted){
      if(this.form.value.checkInType === 'phone')
        checkinInformsation.phoneNumber = this.form.value.checkInInfo.replace(/-/g, '')
      if(this.form.value.checkInType === 'email')
        checkinInformsation.email = this.form.value.checkInInfo

      this.appointmnent.request.set(checkinInformsation)

    } else {
      this.force = true
    }
  }
}
